import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { Alert, Empty, Image, Input, Modal, Skeleton } from 'antd';
import { EnvironmentOutlined, GiftOutlined, IdcardOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Button, FK, PageTitle, Tag } from '../components/ds';
import { authApi } from '../lib/authApi';
import { birthdaySoonLabel, dossierApi, type NetworkCard } from '../lib/api/dossier';

/**
 * «Партнёры сети» (/partners, обращение FB-2, 31.10.2026) — карточки досье других партнёров: фото, имя, город,
 * хобби и увлечения, «10 фактов о себе», день рождения (только день и месяц, без года и возраста).
 * Видят вошедшие партнёры и сотрудники УК; в карточку попадают только те, кто сам открыл досье для партнёров
 * («Моё досье» → «Показывать другим партнёрам»). Ни телефонов, ни почт, ни ИНН, ни денег, ни семьи и достижений здесь нет —
 * сервер их вообще не отдаёт. Все тексты выводятся как обычный текст.
 */

/** Сколько дней вперёд считаем день рождения «ближайшим» для полосы сверху. */
const SOON_DAYS = 14;

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

const textStyle = { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55, fontSize: 14, color: FK.text } as const;

function matches(c: NetworkCard, needle: string): boolean {
  if (!needle) return true;
  return [c.name, c.cities.join(' '), c.hobbies, c.about].join('\n').toLowerCase().includes(needle);
}

export function PartnerNetworkPage(): JSX.Element {
  const meQ = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const q = useQuery({ queryKey: ['wiki', 'dossier', 'network'], queryFn: () => dossierApi.network(), retry: false, staleTime: 60_000 });
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const cards = useMemo<NetworkCard[]>(() => q.data ?? [], [q.data]);
  const needle = search.trim().toLowerCase();
  const shown = useMemo(() => cards.filter((c) => matches(c, needle)), [cards, needle]);
  const soon = useMemo(
    () => cards.filter((c) => c.daysToBirthday !== null && c.daysToBirthday <= SOON_DAYS).sort((a, b) => (a.daysToBirthday ?? 0) - (b.daysToBirthday ?? 0)),
    [cards],
  );
  const canFillMine = !!meQ.data?.dossier?.mine;
  const mineMissing = canFillMine && q.isSuccess && !cards.some((c) => c.isMine);
  const active = cards.find((c) => c.partnerId === openId) ?? null;

  return (
    <div>
      <PageTitle
        title="Партнёры сети"
        sub="Знакомьтесь: чем увлекаются коллеги по франшизе, их факты о себе и дни рождения"
        right={
          canFillMine ? (
            <Button variant="soft" icon={<IdcardOutlined />} to="/dossier">
              Моё досье
            </Button>
          ) : undefined
        }
      />

      {mineMissing ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Вашей карточки здесь пока нет"
          description={
            <>
              Заполните <Link to="/dossier">«Моё досье»</Link> — расскажите о хобби и приложите фото, и коллеги увидят вас в этом разделе.
            </>
          }
        />
      ) : null}

      {q.isLoading ? <Skeleton active paragraph={{ rows: 8 }} /> : null}
      {q.isError ? <Alert type="error" showIcon message="Не удалось загрузить раздел" description={(q.error as Error).message} /> : null}

      {q.isSuccess && soon.length > 0 ? (
        <div
          style={{
            background: FK.orangeSoft,
            border: `1px solid ${FK.border}`,
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: FK.text }}>
            <GiftOutlined style={{ color: FK.orange }} /> Ближайшие дни рождения
          </span>
          {soon.map((c) => (
            <button
              key={c.partnerId}
              type="button"
              onClick={() => setOpenId(c.partnerId)}
              style={{
                border: `1px solid ${FK.border}`,
                background: FK.surface,
                borderRadius: 8,
                padding: '3px 10px',
                cursor: 'pointer',
                fontSize: 13,
                color: FK.text,
              }}
            >
              {c.name} · {c.birthdayLabel} · {birthdaySoonLabel(c.daysToBirthday ?? 0)}
            </button>
          ))}
        </div>
      ) : null}

      {q.isSuccess && cards.length > 0 ? (
        <Input
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchOutlined style={{ color: FK.muted }} />}
          placeholder="Найти по имени, городу или увлечению"
          style={{ maxWidth: 420, marginBottom: 16 }}
        />
      ) : null}

      {q.isSuccess && cards.length === 0 ? (
        <Empty description="Пока никто не открыл своё досье для коллег — станьте первым" />
      ) : null}
      {q.isSuccess && cards.length > 0 && shown.length === 0 ? <Empty description="Никого не нашли — попробуйте другое слово" /> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 16 }}>
        {shown.map((c) => (
          <CardView key={c.partnerId} card={c} onOpen={() => setOpenId(c.partnerId)} />
        ))}
      </div>

      <Modal
        open={!!active}
        onCancel={() => setOpenId(null)}
        footer={null}
        destroyOnHidden
        width={720}
        title={active?.name}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        {active ? <CardDetails card={active} /> : null}
      </Modal>
    </div>
  );
}

function Cover({ card }: { card: NetworkCard }): JSX.Element {
  const first = card.photos[0];
  return first ? (
    <img
      src={dossierApi.photoUrl(card.partnerId, first.id, 'thumb')}
      alt={first.caption ?? card.name}
      loading="lazy"
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  ) : (
    <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: FK.bgSubtle, color: FK.muted, fontSize: 40, fontWeight: 800 }}>
      {initialsOf(card.name)}
    </div>
  );
}

function CardView({ card, onOpen }: { card: NetworkCard; onOpen: () => void }): JSX.Element {
  return (
    <article
      style={{
        background: FK.surface,
        border: `1px solid ${FK.border}`,
        borderRadius: 8,
        boxShadow: FK.shadowCard,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Открыть досье: ${card.name}`}
        style={{ border: 0, padding: 0, background: 'none', cursor: 'pointer', aspectRatio: '4 / 3', width: '100%', display: 'block' }}
      >
        <Cover card={card} />
      </button>
      <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <b style={{ fontSize: 16, color: FK.text }}>{card.name}</b>
          {card.isMine ? <Tag color="orange">Это вы</Tag> : null}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 12.5, color: FK.text2 }}>
          {card.cities.length > 0 ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <EnvironmentOutlined /> {card.cities.join(', ')}
            </span>
          ) : null}
          {card.birthdayLabel ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <GiftOutlined /> {card.birthdayLabel}
            </span>
          ) : null}
        </div>
        {card.hobbies ? (
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: FK.muted, textTransform: 'uppercase', letterSpacing: 0.3 }}>Увлечения</div>
            <div style={{ ...textStyle, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{card.hobbies}</div>
          </div>
        ) : null}
        <div style={{ marginTop: 'auto' }}>
          <Button variant="ghost" size="sm" onClick={onOpen}>
            Читать досье
          </Button>
        </div>
      </div>
    </article>
  );
}

function CardDetails({ card }: { card: NetworkCard }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13.5, color: FK.text2 }}>
        {card.cities.length > 0 ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <EnvironmentOutlined /> {card.cities.join(', ')}
          </span>
        ) : null}
        {card.birthdayLabel ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <GiftOutlined /> День рождения: {card.birthdayLabel}
            {card.daysToBirthday !== null && card.daysToBirthday <= SOON_DAYS ? ` (${birthdaySoonLabel(card.daysToBirthday)})` : ''}
          </span>
        ) : null}
      </div>

      {card.photos.length > 0 ? (
        <Image.PreviewGroup>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))', gap: 8 }}>
            {card.photos.map((p) => (
              <div key={p.id} style={{ minWidth: 0 }}>
                <Image
                  src={dossierApi.photoUrl(card.partnerId, p.id, 'thumb')}
                  alt={p.caption ?? card.name}
                  width="100%"
                  height={130}
                  style={{ objectFit: 'cover', borderRadius: 6 }}
                  preview={{ src: dossierApi.photoUrl(card.partnerId, p.id, 'full') }}
                />
                {p.caption ? <div style={{ fontSize: 12, color: FK.text2, marginTop: 2 }}>{p.caption}</div> : null}
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      ) : null}

      {card.hobbies ? (
        <section>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Хобби и увлечения</h3>
          <div style={textStyle}>{card.hobbies}</div>
        </section>
      ) : null}
      {card.about ? (
        <section>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>10 фактов о себе</h3>
          <div style={textStyle}>{card.about}</div>
        </section>
      ) : null}
      {!card.hobbies && !card.about && card.photos.length === 0 ? <Empty description="Здесь пока только день рождения" /> : null}
    </div>
  );
}
